import React from 'react';
import { Check, Trash2, Ban, CircleDollarSign } from 'lucide-react';
import '@/styles/confirm-dialog.css';

const ICON_MAP = {
  check: Check,
  trash: Trash2,
  ban:   Ban,
  coin:  CircleDollarSign,
};

/**
 * ConfirmDialog — Option A : dialog centré pour actions financières ou destructives.
 *
 * Props:
 *   open         bool     — afficher ou non
 *   onClose      fn       — clic sur Annuler ou backdrop
 *   onConfirm    fn       — clic sur le bouton principal
 *   variant      'financial' | 'destructive'  (défaut: 'financial')
 *   icon         'check' | 'trash' | 'ban' | 'coin'
 *   title        string
 *   subtitle     string   (optionnel)
 *   amount       string   (optionnel, affiché grand en police de titre)
 *   warn         string   (optionnel, texte d'avertissement en rouge)
 *   details      node     (optionnel, bloc d'information sous le montant — ex. aperçu de commission)
 *   confirmLabel string
 *   busy         bool     — désactive le bouton pendant l'appel API
 *   inputPrompt  { label, placeholder } — affiche un textarea optionnel
 *   inputValue   string   — valeur contrôlée du textarea
 *   onInputChange fn      — callback de changement du textarea
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  variant = 'financial',
  icon = 'check',
  title,
  subtitle,
  amount,
  warn,
  details,
  confirmLabel = 'Confirmer',
  busy = false,
  inputPrompt,
  inputValue = '',
  onInputChange,
}) {
  if (!open) return null;

  const HeaderIcon = ICON_MAP[icon] || Check;
  const BtnIcon   = ICON_MAP[icon] || Check;

  return (
    <div className="cdlg-overlay" onClick={onClose}>
      <div className="cdlg-box" onClick={(e) => e.stopPropagation()}>

        <div className={`cdlg-icon cdlg-icon--${variant}`}>
          <HeaderIcon size={28} strokeWidth={2} />
        </div>

        <div className="cdlg-title">{title}</div>

        {subtitle && <div className="cdlg-sub">{subtitle}</div>}

        {amount && <div className="cdlg-amount">{amount}</div>}
        {details && <div className="cdlg-details">{details}</div>}
        {warn   && <div className="cdlg-warn">{warn}</div>}

        {inputPrompt && (
          <div className="cdlg-input-group">
            <label className="cdlg-input-label">{inputPrompt.label}</label>
            <textarea
              className="cdlg-textarea"
              placeholder={inputPrompt.placeholder || ''}
              value={inputValue}
              onChange={(e) => onInputChange?.(e.target.value)}
              rows={3}
              autoFocus
            />
          </div>
        )}

        <div className="cdlg-divider" />

        <div className="cdlg-btns">
          <button
            type="button"
            className={`cdlg-btn cdlg-btn--${variant}`}
            onClick={onConfirm}
            disabled={busy}
            data-testid="cdlg-confirm"
          >
            <BtnIcon size={16} strokeWidth={2.5} />
            {confirmLabel}
          </button>
          <button
            type="button"
            className="cdlg-btn cdlg-btn--cancel"
            onClick={onClose}
            disabled={busy}
          >
            Annuler
          </button>
        </div>

      </div>
    </div>
  );
}
